import { useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { SelectEntidade } from "@/components/SelectEntidade";
import { InfoField } from "@/components/InfoField";
import { capitalizar } from "@/utils/format";
import {
  formatDataBR,
  type EspelhoCarga,
  type EspelhoColaborador,
  type EspelhoDia,
  type EspelhoFeriado,
} from "@/api/escala.service";
import { ROTULO_BLOCO, blocoDoDia, blocoVazio, formatarBloco, motivoCargaDoDia } from "@/components/escala/cargaHoraria";

// Ou a carga escolhida, ou a volta à carga padrão do colaborador.
export type EscolhaCargaDia = { carga_horaria_id: string } | { padrao: true };

interface EscalaCargaDiaModalProps {
  // Dia trabalhado (T ou "?") cuja carga muda; null fecha a janela.
  dia: EspelhoDia | null;
  colaborador?: EspelhoColaborador;
  cargas: EspelhoCarga[];
  feriados: EspelhoFeriado[];
  onClose: () => void;
  // Rejeita quando o servidor recusa: a janela continua aberta.
  onConfirmar: (escolha: EscolhaCargaDia) => Promise<void>;
}

// Troca a carga horária de um dia: as cargas da escala, com o horário do bloco do dia, e as que não
// servem a ele desabilitadas com o motivo.
export function EscalaCargaDiaModal({ dia, colaborador, cargas, feriados, onClose, onConfirmar }: EscalaCargaDiaModalProps) {
  const [cargaId, setCargaId] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => setCargaId(dia?.carga_horaria_id ?? null), [dia]);

  const opcoes = useMemo(
    () => cargas.filter((c) => c.da_escala).sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR")),
    [cargas],
  );
  if (!dia) return null;

  const bloco = blocoDoDia(dia.data, feriados);
  const feriado = feriados.find((f) => f.data === dia.data);
  const porId = new Map(cargas.map((c) => [c.id, c]));
  const atual = dia.carga_horaria_id ? porId.get(dia.carga_horaria_id) : undefined;
  const padraoId = colaborador?.carga_horaria_id;
  const padrao = padraoId ? porId.get(padraoId) : undefined;
  const horarioAtual = dia.horario ? `${atual?.descricao ?? "Carga"} — ${formatarBloco(dia.horario)}` : "Sem horário (?)";
  // O dia trocado à mão volta ao padrão. O "?" também, quando a padrão já tem horário para o dia: o
  // feriado mudou depois da geração, ou o rascunho é de um mês que o reflexo do cadastro não alcança.
  const padraoServe = !!padrao && !motivoCargaDoDia(padrao, bloco, padrao.id);
  const acaoPadrao = dia.manual ? "Voltar ao padrão" : !dia.horario && padraoServe ? "Aplicar a carga padrão" : undefined;

  const confirmar = async (escolha: EscolhaCargaDia) => {
    setSalvando(true);
    try {
      await onConfirmar(escolha);
    } catch {
      // Quem chama já avisou o erro; a janela fica aberta para outra escolha.
    } finally {
      setSalvando(false);
    }
  };

  const rotulo = (c: EspelhoCarga) => {
    const horario = blocoVazio(c[bloco]) ? "" : ` — ${formatarBloco(c[bloco])}`;
    return `${c.descricao}${horario}${c.id === padraoId ? " (padrão)" : ""}`;
  };
  const motivo = (c: EspelhoCarga) => {
    const m = motivoCargaDoDia(c, bloco, dia.carga_horaria_id);
    return m ? capitalizar(m) : undefined;
  };

  return (
    <Dialog open onClose={salvando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Horário do dia</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Stack direction="row" flexWrap="wrap" columnGap={4} rowGap={1.5}>
          <InfoField label="Colaborador" value={colaborador?.nome} />
          <InfoField label="Dia" value={formatDataBR(dia.data)} />
          <InfoField label="Tipo do dia" value={feriado ? `${ROTULO_BLOCO[bloco]} (${feriado.nome})` : ROTULO_BLOCO[bloco]} />
        </Stack>
        <Stack direction="row" flexWrap="wrap" columnGap={4} rowGap={1.5}>
          <InfoField label="Horário atual" value={horarioAtual} />
          <InfoField label="Carga padrão" value={padraoId ? padrao?.descricao : "Sem carga padrão"} />
        </Stack>
        <SelectEntidade
          label="Carga horária do dia"
          options={opcoes}
          value={cargaId}
          onChange={setCargaId}
          getLabel={rotulo}
          getDisabledReason={motivo}
          disabled={salvando}
          required
        />
      </DialogContent>
      <DialogActions>
        {acaoPadrao && (
          <Button onClick={() => confirmar({ padrao: true })} disabled={salvando} sx={{ mr: "auto" }}>
            {acaoPadrao}
          </Button>
        )}
        <Button variant="outlined" onClick={onClose} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => cargaId && confirmar({ carga_horaria_id: cargaId })}
          disabled={!cargaId || cargaId === dia.carga_horaria_id || salvando}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
